import { Switch as HeadlessSwitch } from '@headlessui/react';
import { cn } from '@lib/utils/cn';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /**
   * `default` es el switch primario historico. `brand` es el toggle de Claude
   * Design (gradiente teal→violeta al estar activo), usado en Configuración.
   * El default no cambia, asi que las pantallas que ya usaban Switch quedan
   * igual.
   */
  variant?: 'default' | 'brand';
  'data-testid'?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
  variant = 'default',
  'data-testid': testId,
}: SwitchProps) {
  const sizes = {
    sm: {
      switch: 'h-5 w-9',
      dot: 'h-4 w-4',
      translate: 'translate-x-4',
    },
    md: {
      switch: 'h-6 w-11',
      dot: 'h-5 w-5',
      translate: 'translate-x-5',
    },
  };

  return (
    <div className="flex items-center gap-3">
      <HeadlessSwitch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={testId}
        className={cn(
          'relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          sizes[size].switch,
          checked
            ? variant === 'brand'
              ? 'bg-gradient-to-br from-teal-500 to-[#6b239e]'
              : 'bg-primary-600'
            : 'bg-secondary-300 dark:bg-secondary-600',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none inline-block rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
            sizes[size].dot,
            checked ? sizes[size].translate : 'translate-x-0'
          )}
        />
      </HeadlessSwitch>
      {label && (
        <span
          onClick={() => !disabled && onChange(!checked)}
          className={cn(
            'text-sm font-medium text-secondary-900 dark:text-white cursor-pointer select-none',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
}
